import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  Chip,
  Tooltip,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import axios from 'axios';

const OrderManagement = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Form state
  const [formData, setFormData] = useState({
    product_code: '',
    quantity: '',
    customer_id: '',
    machine_id: '',
    commessa: '',
    reference: '',
    notes: ''
  });

  useEffect(() => {
    loadOrders();
    loadCustomers();
    loadProducts();
    loadMachines();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Errore nel caricamento ordini:', error);
      showSnackbar('Errore nel caricamento ordini', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await axios.get('/api/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Errore nel caricamento clienti:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get('/api/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Errore nel caricamento prodotti:', error);
    }
  };

  const loadMachines = async () => {
    try {
      const response = await axios.get('/api/machines');
      setMachines(response.data);
    } catch (error) {
      console.error('Errore nel caricamento macchine:', error);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = (order = null) => {
    if (order) {
      setEditingOrder(order);
      setFormData({
        product_code: order.product_code,
        quantity: order.quantity.toString(),
        customer_id: order.customer_id || '',
        machine_id: order.machine_id || '',
        commessa: order.commessa || '',
        reference: order.reference || '',
        notes: order.notes || ''
      });
    } else {
      setEditingOrder(null);
      setFormData({
        product_code: '',
        quantity: '',
        customer_id: '',
        machine_id: '',
        commessa: '',
        reference: '',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingOrder(null);
    setFormData({
      product_code: '',
      quantity: '',
      customer_id: '',
      machine_id: '',
      commessa: '',
      reference: '',
      notes: ''
    });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.product_code || !formData.quantity) {
        showSnackbar('Compila tutti i campi obbligatori (Prodotto e Quantità)', 'error');
        return;
      }

      const orderData = {
        product_code: formData.product_code,
        quantity: parseInt(formData.quantity),
        customer_id: formData.customer_id || null,
        machine_id: formData.machine_id || null,
        commessa: formData.commessa || null,
        reference: formData.reference,
        notes: formData.notes
      };

      if (editingOrder) {
        await axios.put(`/api/orders/${editingOrder.id}`, orderData);
        showSnackbar('Ordine aggiornato con successo', 'success');
      } else {
        await axios.post('/api/orders', orderData);
        showSnackbar('Ordine creato con successo', 'success');
      }

      handleCloseDialog();
      loadOrders();
    } catch (error) {
      console.error('Errore nel salvataggio ordine:', error);
      showSnackbar('Errore nel salvataggio ordine', 'error');
    }
  };

  const handleDelete = async (orderId) => {
    if (window.confirm('Sei sicuro di voler eliminare questo ordine?')) {
      try {
        await axios.delete(`/api/orders/${orderId}`);
        showSnackbar('Ordine eliminato con successo', 'success');
        loadOrders();
      } catch (error) {
        console.error('Errore nell\'eliminazione ordine:', error);
        showSnackbar('Errore nell\'eliminazione ordine', 'error');
      }
    }
  };

  const handleSendToProduction = async (orderId) => {
    try {
      await axios.post(`/api/orders/${orderId}/send-to-production`);
      showSnackbar('Ordine inviato alla produzione', 'success');
      loadOrders();
    } catch (error) {
      console.error('Errore nell\'invio alla produzione:', error);
      showSnackbar('Errore nell\'invio alla produzione', 'error');
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      'pending': { label: 'In Attesa', color: 'default' },
      'sent_to_machine': { label: 'Inviato a Macchina', color: 'info' },
      'in_production': { label: 'In Produzione', color: 'primary' },
      'completed': { label: 'Completato', color: 'success' },
      'cancelled': { label: 'Annullato', color: 'error' },
      'error': { label: 'Errore', color: 'error' }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'commessa', headerName: 'Commessa', width: 120, valueGetter: (params) => params.row.commessa || '-' },
    { field: 'product_code', headerName: 'Codice Prodotto', width: 130 },
    {
      field: 'product_description',
      headerName: 'Descrizione',
      width: 150,
      valueGetter: (params) => params.row.product_description || '-'
    },
    { field: 'reference', headerName: 'Riferimento', width: 120, valueGetter: (params) => params.row.reference || '-' },
    { field: 'quantity', headerName: 'Quantità', width: 90, type: 'number' },
    {
      field: 'customer_name',
      headerName: 'Cliente',
      width: 150,
      valueGetter: (params) => {
        const customer = customers.find(c => c.id === params.row.customer_id);
        return customer ? customer.name : '-';
      }
    },
    {
      field: 'machine_name',
      headerName: 'Macchina',
      width: 150,
      valueGetter: (params) => {
        const machine = machines.find(m => m.id === params.row.machine_id);
        return machine ? machine.name : '-';
      }
    },
    {
      field: 'status',
      headerName: 'Stato',
      width: 130,
      renderCell: (params) => getStatusChip(params.value)
    },
    {
      field: 'created_at',
      headerName: 'Data Creazione',
      width: 150,
      valueFormatter: (params) => {
        return new Date(params.value).toLocaleDateString('it-IT');
      }
    },
    {
      field: 'sent_to_production_at',
      headerName: 'Data Invio',
      width: 150,
      valueFormatter: (params) => {
        return params.value ? new Date(params.value).toLocaleDateString('it-IT') : '-';
      }
    },
    {
      field: 'good_pieces',
      headerName: 'Pezzi Buoni',
      width: 110,
      type: 'number',
      valueGetter: (params) => params.row.good_pieces || params.row.produced_quantity || 0
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      width: 200,
      sortable: false,
      renderCell: (params) => {
        const canSendToProduction = params.row.status === 'pending';
        const canEdit = params.row.status === 'pending';
        const canDelete = params.row.status === 'pending';

        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Dettaglio ordine">
              <IconButton
                size="small"
                color="secondary"
                onClick={() => navigate(`/orders/${params.row.id}`)}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
            {canSendToProduction && (
              <Tooltip title="Invia alla produzione">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleSendToProduction(params.row.id)}
                >
                  <PlayArrowIcon />
                </IconButton>
              </Tooltip>
            )}
            {canEdit && (
              <Tooltip title="Modifica">
                <IconButton
                  size="small"
                  color="info"
                  onClick={() => handleOpenDialog(params.row)}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Elimina">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(params.row.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      }
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Gestione Ordini
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadOrders}
            >
              Aggiorna
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Nuovo Ordine
            </Button>
          </Box>
        </Box>

        <Box sx={{ height: 400, width: '100%' }}>
          <DataGrid
            rows={orders}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25]}
            disableSelectionOnClick
            localeText={{
              noRowsLabel: 'Nessun ordine trovato',
              footerRowSelected: (count) => `${count} riga${count !== 1 ? 'he' : ''} selezionata${count !== 1 ? 'e' : ''}`,
              footerTotalRows: 'Righe totali:',
              columnMenuSortAsc: 'Ordine crescente',
              columnMenuSortDesc: 'Ordine decrescente',
              columnMenuFilter: 'Filtro',
              columnMenuHideColumn: 'Nascondi colonna',
              columnMenuShowColumns: 'Mostra colonne'
            }}
          />
        </Box>

        {/* Dialog per aggiungere/modificare ordine */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingOrder ? 'Modifica Ordine' : 'Nuovo Ordine'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Autocomplete
                fullWidth
                options={products}
                getOptionLabel={(option) => `${option.code} - ${option.description || 'Senza descrizione'}`}
                value={products.find(p => p.code === formData.product_code) || null}
                onChange={(e, newValue) => setFormData({ ...formData, product_code: newValue?.code || '' })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Prodotto"
                    required
                    helperText="Seleziona un prodotto dal catalogo"
                  />
                )}
                freeSolo
                onInputChange={(e, newValue) => {
                  if (!products.find(p => p.code === newValue)) {
                    setFormData({ ...formData, product_code: newValue });
                  }
                }}
              />
              <TextField
                fullWidth
                label="Commessa"
                value={formData.commessa}
                onChange={(e) => setFormData({ ...formData, commessa: e.target.value })}
                helperText="Numero commessa (opzionale)"
              />
              <TextField
                fullWidth
                label="Riferimento"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                helperText="Riferimento ordine cliente (opzionale)"
              />
              <TextField
                fullWidth
                label="Quantità"
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                required
                inputProps={{ min: 1 }}
              />
              <TextField
                fullWidth
                select
                label="Cliente"
                value={formData.customer_id}
                onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                helperText="Opzionale"
              >
                <MenuItem value="">
                  <em>Nessun cliente</em>
                </MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                select
                label="Macchina"
                value={formData.machine_id}
                onChange={(e) => setFormData({ ...formData, machine_id: e.target.value })}
                helperText="Opzionale - Assegna a macchina"
              >
                <MenuItem value="">
                  <em>Nessuna macchina</em>
                </MenuItem>
                {machines.filter(m => m.is_active).map((machine) => (
                  <MenuItem key={machine.id} value={machine.id}>
                    {machine.name} ({machine.code})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Note"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annulla</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingOrder ? 'Aggiorna' : 'Crea'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar per messaggi */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </LocalizationProvider>
  );
};

export default OrderManagement;